import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export type CheckoutMessageType = "success" | "error" | "info";

export interface CheckoutStatus {
  message: string;
  messageType: CheckoutMessageType;
  setMessage: (message: string) => void;
  setMessageType: (type: CheckoutMessageType) => void;
  clearMessage: () => void;
}

/**
 * Shared hook for handling Stripe checkout success/cancel URL parameters.
 * Used by SubscriptionPricing and SubscriptionPrompt components.
 *
 * @param onSuccess - Optional callback when checkout is successful
 * @returns CheckoutStatus object with message state and setters
 */
export function useCheckoutStatus(onSuccess?: () => void): CheckoutStatus {
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<CheckoutMessageType>("info");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("success")) {
      setMessage(
        "Order placed! You will receive an email confirmation shortly.",
      );
      setMessageType("success");
      if (onSuccess) {
        onSuccess();
      }
    }
    if (searchParams.get("canceled")) {
      setMessage(
        "Order canceled — continue to shop around and checkout when you're ready.",
      );
      setMessageType("info");
    }
  }, [searchParams, onSuccess]);

  const clearMessage = () => {
    setMessage("");
    setMessageType("info");
  };

  return {
    message,
    messageType,
    setMessage,
    setMessageType,
    clearMessage,
  };
}
